// packages/core/src/learning/core-modules/big-life-decisions.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 11: "Big Life Decisions" (tier: life-design)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Verified refs:
//   openImpactLab categoryId 'big-decisions' (valid ImpactCategoryId)
//   openImpactScenario 'move-out-vs-runway', 'new-vs-used-car'
// Note: tier 'life-design' and the school tag 'life-design' are spelled the
// same but are different unions; both are valid.
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'big-life-decisions--big-decisions-have-hidden-timelines',
	title: 'Big decisions have hidden timelines',
	description:
		'You learn to see major life choices as timeline decisions, not just monthly payment decisions.',
	pillar: 'life-decisions',
	schoolsOfThought: ['life-design', 'fi-math', 'money-psychology-housel'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['big-decisions', 'life-design', 'tradeoffs', 'freedom-cost'],
	prerequisites: [],
	relatedLessons: ['big-life-decisions--moving-out-costs-more-than-rent'],
	concepts: ['decision-timelines', 'hidden-costs', 'freedom-cost'],
	blocks: [
		{
			type: 'paragraph',
			text: 'A big life decision rarely looks like a spreadsheet at first. It looks like wanting your own space, needing a car, moving cities, helping family, changing jobs, or finally upgrading your life. The decision feels personal before it feels financial.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is urgency. “I need this now.” “I deserve this.” “Everyone else is doing it.” “I am tired of waiting.” Sometimes the feeling is valid. But urgency can make a permanent cost look smaller than it really is.',
		},
		{
			type: 'bullet_list',
			items: [
				'Monthly payment',
				'Upfront cost',
				'New fixed bills',
				'Lost savings capacity',
				'Reduced runway',
				'Future flexibility',
			],
		},
		{
			type: 'callout',
			icon: '🧭',
			text: 'The real question is not only “Can I afford this?” It is “What does this do to my options?”',
		},
		{
			type: 'example',
			label: 'The $400 decision',
			text: 'A $400 monthly commitment is not just $400. It is $4,800 per year of future cash flow already spoken for. That might be worth it. But it also means less room for emergencies, investing, moving, debt progress, or career flexibility.',
		},
		{
			type: 'paragraph',
			text: 'This is the freedom cost. A choice can be affordable and still expensive in time. If it raises your monthly baseline, it may increase the income you need to feel safe. If it drains your cash, it may reduce your runway. If it locks you into a place, car, lease, or lifestyle, it may reduce your ability to pivot.',
		},
		{
			type: 'paragraph',
			text: 'The honest tradeoff is that big decisions often buy something real. Comfort, independence, safety, identity, family stability, better work access, or quality of life. Nisk’s angle is not “never do it.” It is “see the cost clearly before the decision owns your future.”',
		},
		{
			type: 'callout',
			icon: '❓',
			text: 'Reflection question: If this decision goes through, what option becomes harder for the next 6–24 months?',
		},
		{
			type: 'key_takeaway',
			text: 'Big decisions are not just purchases. They are timelines with hidden commitments attached.',
		},
	],
	audioScript:
		'Big life decisions usually do not feel like math at first. They feel emotional. You want your own space. You need reliable transportation. You want to leave a city. You want to help family. You want a better lifestyle. You want proof that your life is moving forward. None of that is fake. These decisions matter because they touch identity, comfort, safety, and independence. But that is exactly why they need better math. The trap is urgency. When you feel behind, tired, embarrassed, or ready for a change, a permanent cost can look smaller than it really is. A $400 monthly payment sounds like one number. But it is also $4,800 per year of cash flow already committed. That money cannot also build runway, reduce debt pressure, fund a move, support investing, or buy time while you switch jobs. That is the freedom cost. A decision can be technically affordable and still reduce your options. This does not mean big choices are bad. Moving out can buy peace. A car can buy access. A better location can improve your life. Helping family can matter deeply. The point is to see the full timeline, not just the monthly bill. Ask what the decision gives you, what it costs now, what it costs later, and what becomes harder afterward. That is how big choices become clearer.',
	flashcards: [
		{
			id: 'big-life-decisions--big-decisions-have-hidden-timelines--fc-main',
			role: 'main_idea',
			front: 'What is a hidden timeline?',
			back: 'The future cash flow, runway, and flexibility a big decision affects.',
		},
		{
			id: 'big-life-decisions--big-decisions-have-hidden-timelines--fc-watch',
			role: 'watch_out',
			front: 'What makes big decisions risky?',
			back: 'Urgency can make permanent costs look smaller than they are.',
		},
		{
			id: 'big-life-decisions--big-decisions-have-hidden-timelines--fc-use',
			role: 'use_it',
			front: 'What question helps?',
			back: 'Ask what option becomes harder after the decision.',
		},
	],
	applyAction: {
		type: 'openImpactLab',
		label: 'Explore big decision tradeoffs',
		categoryId: 'big-decisions',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'big-life-decisions--moving-out-costs-more-than-rent',
	title: 'Moving out costs more than rent',
	description:
		'You learn how independence, rent, deposits, bills, and lost runway all interact when deciding whether to move out.',
	pillar: 'life-decisions',
	schoolsOfThought: [
		'life-design',
		'real-estate-housing',
		'adulting-life-skills',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['moving-out', 'rent', 'runway', 'adulting'],
	prerequisites: ['big-life-decisions--big-decisions-have-hidden-timelines'],
	relatedLessons: ['big-life-decisions--car-payment-buys-more-than-a-car'],
	concepts: ['move-out-math', 'runway-tradeoff', 'independence-cost'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Moving out is not just a rent decision. It is an independence decision, privacy decision, family decision, commute decision, lifestyle decision, and cash-flow decision all at once. That is why it can feel so emotional.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is treating moving out as proof that you are finally an adult. Sometimes moving out is healthy and necessary. Sometimes staying longer builds power. The danger is making the choice from shame instead of clarity.',
		},
		{
			type: 'bullet_list',
			items: [
				'Rent',
				'Deposit',
				'Utilities',
				'Furniture',
				'Groceries',
				'Transportation',
				'Renter insurance',
				'Emergency buffer',
			],
		},
		{
			type: 'callout',
			icon: '🏠',
			text: 'Rent is only the headline cost. The full move-out cost is the new monthly life you are signing up for.',
		},
		{
			type: 'example',
			label: 'Rent plus reality',
			text: 'A $1,600 apartment may become $2,100 or more after utilities, internet, groceries, parking, supplies, furniture, and higher transportation costs. That does not make moving out wrong. It means the real decision is bigger than rent.',
		},
		{
			type: 'paragraph',
			text: 'The freedom cost is runway. If moving out drops your monthly surplus from $700 to $100, your ability to handle emergencies, pay down debt, invest, move again, or leave a bad job changes. The rent bought independence, but it also bought a tighter margin.',
		},
		{
			type: 'paragraph',
			text: 'The honest tradeoff is real. Moving out can buy peace, safety, privacy, focus, dating freedom, work access, and self-respect. Staying longer can buy runway, debt progress, investing room, and a cleaner launch. Neither path is automatically mature. The mature part is seeing the tradeoff.',
		},
		{
			type: 'callout',
			icon: '❓',
			text: 'Reflection question: Are you moving toward a better life, or away from an uncomfortable feeling?',
		},
		{
			type: 'key_takeaway',
			text: 'Moving out is strongest when the independence is worth the runway it costs.',
		},
	],
	audioScript:
		'Moving out is one of those decisions where the math and the emotion are completely tangled. On paper, it looks like rent. In real life, it can mean freedom, privacy, peace, distance from family tension, dating independence, shorter commute, better focus, or finally feeling like your own adult. That emotional side matters. But the math is bigger than rent. A $1,600 apartment is not just $1,600. Add utilities, internet, groceries, furniture, supplies, transportation, parking, insurance, deposits, and the emergency buffer you now need because the bills are yours. Suddenly the real monthly change is much bigger. The trap is making the decision from shame. “I should be out by now.” “People will judge me.” “I need to prove I am grown.” That pressure can push you into a life that looks independent but feels financially trapped. On the other side, staying too long can also have a cost. It may hurt your peace, confidence, relationships, or growth. So the point is not “move” or “do not move.” The point is to compare what each path buys and what each path costs. Moving out may buy independence. Staying may buy runway. Moving out may give peace. Staying may speed up debt progress. Moving out may improve your life now. Staying may create a stronger launch later. The right insight is clarity: independence is valuable, but it has a monthly timeline attached.',
	flashcards: [
		{
			id: 'big-life-decisions--moving-out-costs-more-than-rent--fc-main',
			role: 'main_idea',
			front: 'Why is moving out bigger than rent?',
			back: 'Because it changes bills, runway, flexibility, privacy, and monthly baseline.',
		},
		{
			id: 'big-life-decisions--moving-out-costs-more-than-rent--fc-watch',
			role: 'watch_out',
			front: 'What is the move-out trap?',
			back: 'Moving from shame or pressure without seeing the full cost.',
		},
		{
			id: 'big-life-decisions--moving-out-costs-more-than-rent--fc-use',
			role: 'use_it',
			front: 'What should be compared?',
			back: 'Compare independence gained against runway, surplus, and flexibility lost.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Compare move-out vs runway',
		scenarioId: 'move-out-vs-runway',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'big-life-decisions--car-payment-buys-more-than-a-car',
	title: 'The car payment buys more than a car',
	description:
		'You learn how car decisions affect access, safety, monthly baseline, and future flexibility.',
	pillar: 'life-decisions',
	schoolsOfThought: ['adulting-life-skills', 'frugality', 'life-design'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['car-decision', 'car-payment', 'transportation', 'big-purchases'],
	prerequisites: ['big-life-decisions--moving-out-costs-more-than-rent'],
	relatedLessons: ['big-life-decisions--location-changes-your-whole-budget'],
	concepts: ['car-baseline', 'transportation-tradeoff', 'payment-drag'],
	blocks: [
		{
			type: 'paragraph',
			text: 'A car decision looks simple until it hits real life. You need transportation. You want reliability. You may care about safety, image, commute, family, dating, work access, or not being stuck. A car can genuinely improve your options.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is turning transportation into identity too quickly. A nicer car can feel like progress. It can feel like proof. It can feel like a reward. But if the payment becomes heavy, the car can quietly take options away.',
		},
		{
			type: 'bullet_list',
			items: [
				'Payment',
				'Insurance',
				'Gas',
				'Maintenance',
				'Registration',
				'Repairs',
				'Depreciation',
				'Parking',
			],
		},
		{
			type: 'callout',
			icon: '🚗',
			text: 'The car price is not the full car cost. The monthly baseline is the real pressure point.',
		},
		{
			type: 'example',
			label: 'Payment gap',
			text: 'A newer car might create a $550 monthly payment while a cheaper reliable car creates a $250 monthly payment. That $300 gap is $3,600 per year before counting insurance differences, repairs, and other costs. The gap is not just money. It is flexibility.',
		},
		{
			type: 'paragraph',
			text: 'The freedom cost is payment drag. A car payment follows you even during a bad month. If it eats the surplus that could build runway, reduce debt, or fund investing, the vehicle may be reliable while your financial life becomes less flexible.',
		},
		{
			type: 'paragraph',
			text: 'The honest tradeoff is that cheaper is not always better. An unreliable car can cost money, time, stress, missed work, and safety. A more expensive car can be worth understanding when it reduces real risk or supports work access. The key is not cheapest or flashiest. It is total cost versus real usefulness.',
		},
		{
			type: 'callout',
			icon: '❓',
			text: 'Reflection question: Is this car mainly solving a transportation problem, or an identity feeling?',
		},
		{
			type: 'key_takeaway',
			text: 'A car can buy access, but the payment can sell away flexibility.',
		},
	],
	audioScript:
		'A car is not just a car. It is access. It gets you to work, school, family, the gym, dates, errands, and opportunities. Reliable transportation can genuinely change your life. So this is not a “never buy a decent car” lesson. The real lesson is that a car decision has more costs than the sticker price. There is the payment, insurance, gas, maintenance, registration, repairs, parking, and depreciation. More importantly, there is the monthly baseline. If your car costs too much every month, it can trap your cash flow even if you technically love the car. The emotional trap is identity. A nicer car can feel like progress, especially if you have worked hard or felt behind. It can feel like proof that you are doing better. But the payment does not care about that feeling. It keeps showing up. A $300 monthly gap between two car choices is $3,600 per year. That might be the difference between building runway and staying fragile. At the same time, cheap can also be expensive if the car is unsafe, unreliable, or constantly breaking. The stronger way to think is total usefulness versus total cost. What does the car help you do? What risks does it reduce? What does it add to your monthly baseline? What options does the payment make harder? A good transportation decision solves a real life problem without quietly taking over your future cash flow.',
	flashcards: [
		{
			id: 'big-life-decisions--car-payment-buys-more-than-a-car--fc-main',
			role: 'main_idea',
			front: 'What is payment drag?',
			back: 'A recurring car cost that reduces monthly flexibility.',
		},
		{
			id: 'big-life-decisions--car-payment-buys-more-than-a-car--fc-watch',
			role: 'watch_out',
			front: 'What is the car trap?',
			back: 'Letting identity or status hide the full monthly cost.',
		},
		{
			id: 'big-life-decisions--car-payment-buys-more-than-a-car--fc-use',
			role: 'use_it',
			front: 'What should car math include?',
			back: 'Payment, insurance, fuel, repairs, maintenance, registration, and flexibility cost.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Compare new vs used car cost',
		scenarioId: 'new-vs-used-car',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'big-life-decisions--location-changes-your-whole-budget',
	title: 'Location changes your whole budget',
	description:
		'You learn how moving cities, states, or countries changes income, expenses, relationships, safety, and opportunity.',
	pillar: 'life-decisions',
	schoolsOfThought: [
		'life-design',
		'real-estate-housing',
		'relationship-family-money',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['relocation', 'living-abroad', 'cost-of-living', 'life-design'],
	prerequisites: ['big-life-decisions--car-payment-buys-more-than-a-car'],
	relatedLessons: ['big-life-decisions--life-upgrades-need-a-tradeoff'],
	concepts: ['location-math', 'lifestyle-fit', 'geographic-tradeoffs'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Location is one of the biggest money decisions because it changes almost everything at once. Rent, transportation, taxes, food, healthcare access, dating, family support, income options, safety, lifestyle, and social life can all shift when you move.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is fantasy. A cheaper city can look like instant freedom. A famous city can look like instant opportunity. Living abroad can look like a full reset. Sometimes those moves help. But every location has tradeoffs hiding behind the highlight reel.',
		},
		{
			type: 'bullet_list',
			items: [
				'Income potential',
				'Housing cost',
				'Transportation',
				'Healthcare access',
				'Safety',
				'Social support',
				'Visa or legal limits',
				'Career path',
			],
		},
		{
			type: 'callout',
			icon: '🌍',
			text: 'A cheaper place is not automatically a freer place. Freedom depends on the whole life system.',
		},
		{
			type: 'example',
			label: 'Cheaper rent, different costs',
			text: 'A move that cuts rent by $700 can be powerful. But if it also lowers income, increases transportation costs, isolates you, or weakens career options, the savings may not tell the whole story. The math and the life fit both matter.',
		},
		{
			type: 'paragraph',
			text: 'The freedom cost is not only money. A location can give you runway but reduce opportunity. It can give you opportunity but raise your baseline. It can give you community but reduce privacy. It can give you adventure but add legal, family, healthcare, or work complications.',
		},
		{
			type: 'paragraph',
			text: 'The honest tradeoff is that location can be one of the strongest life-design levers. A better-fit place can lower pressure, improve lifestyle, create focus, or open doors. But the decision deserves more than vibes. It needs a full picture of what changes in real life.',
		},
		{
			type: 'callout',
			icon: '❓',
			text: 'Reflection question: What does this place make easier, and what does it quietly make harder?',
		},
		{
			type: 'key_takeaway',
			text: 'Location is not just where you live. It is the cost structure your life runs on.',
		},
	],
	audioScript:
		'Location changes your whole budget because location changes your whole life. Rent is the obvious part, but it is not the only part. Moving cities, states, or countries can change income, transportation, taxes, food costs, healthcare access, dating, family support, safety, career options, and your social life. That is why location decisions are powerful, but also easy to romanticize. A cheaper place can look like instant freedom. A big city can look like instant success. Living abroad can look like a full reset. Sometimes those stories are partly true. But no location is only its best feature. A cheaper city may give you runway but reduce your income path. A high-opportunity city may grow your career but raise your baseline. Living abroad may lower spending and improve lifestyle, but add visa rules, healthcare complexity, family distance, language issues, or work limits. The real question is not “Is this place cheap?” The better question is, “What does this place make easier, and what does it make harder?” A place is a life system. It affects how much you need, how much you can earn, who you see, how safe you feel, how easily you grow, and what kind of person your routine supports. Location can be one of the strongest life-design levers. Just do not judge it by rent alone or fantasy alone. Judge the whole tradeoff.',
	flashcards: [
		{
			id: 'big-life-decisions--location-changes-your-whole-budget--fc-main',
			role: 'main_idea',
			front: 'Why does location matter?',
			back: 'It changes income, expenses, support, safety, lifestyle, and opportunity.',
		},
		{
			id: 'big-life-decisions--location-changes-your-whole-budget--fc-watch',
			role: 'watch_out',
			front: 'What is the location trap?',
			back: 'Judging a place by fantasy, rent, or status instead of the full system.',
		},
		{
			id: 'big-life-decisions--location-changes-your-whole-budget--fc-use',
			role: 'use_it',
			front: 'What should you ask?',
			back: 'Ask what the location makes easier and what it makes harder.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Map a location tradeoff',
		prompt: 'Name one place you might live and list what it makes easier, what it makes harder, and what it changes financially.',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'big-life-decisions--life-upgrades-need-a-tradeoff',
	title: 'Life upgrades need a tradeoff',
	description:
		'You learn how to evaluate major upgrades by what they improve, what they cost, and what future option they delay.',
	pillar: 'life-decisions',
	schoolsOfThought: ['rich-life-ramit', 'life-design', 'fi-math'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['life-upgrades', 'spending', 'tradeoffs', 'values'],
	prerequisites: ['big-life-decisions--location-changes-your-whole-budget'],
	relatedLessons: ['big-life-decisions--big-decisions-have-hidden-timelines'],
	concepts: ['upgrade-tradeoffs', 'values-spending', 'delayed-options'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Some upgrades are worth money. Better housing, safer transportation, therapy, fitness, travel, education, childcare, tools, family help, or a better work setup can genuinely improve life. The point is not to reject upgrades. The point is to make them compete honestly.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is calling every upgrade “self-care,” “investment,” or “necessary” without testing the tradeoff. Those words can be true. They can also become cover for avoiding the cost.',
		},
		{
			type: 'bullet_list',
			items: [
				'What improves now',
				'What becomes recurring',
				'What cash gets used',
				'What goal slows down',
				'What stress goes down',
				'What flexibility goes down',
			],
		},
		{
			type: 'callout',
			icon: '⚖️',
			text: 'A good upgrade gives enough life value to justify the option it delays.',
		},
		{
			type: 'example',
			label: 'The $500 upgrade',
			text: 'A $500 monthly lifestyle upgrade might make life better. It may also mean $6,000 per year that cannot build runway, fund investing, reduce debt pressure, or support a future move. The question is whether the improvement is worth that tradeoff.',
		},
		{
			type: 'paragraph',
			text: 'The freedom cost is delayed options. Every meaningful upgrade has an opportunity cost. Not in a guilt-based way. In a real way. Money pointed at one life improvement cannot point at every other goal at the same time.',
		},
		{
			type: 'paragraph',
			text: 'The honest tradeoff is that future freedom is not the only thing that matters. Present quality of life matters too. A plan that ignores your actual life can become brittle. But upgrades need to be chosen, not absorbed automatically through pressure, comparison, or stress.',
		},
		{
			type: 'callout',
			icon: '❓',
			text: 'Reflection question: Would you still choose this upgrade if the delayed goal were visible on the price tag?',
		},
		{
			type: 'key_takeaway',
			text: 'The best upgrades are not guilt-free because they are cheap. They are clear because the tradeoff is worth it.',
		},
	],
	audioScript:
		'Life upgrades are not the enemy. This matters because a lot of money content makes it sound like every upgrade is weakness. That is too simple. A better apartment can improve your peace. A reliable car can improve your access. Therapy, fitness, tools, education, childcare, travel, or family support can be worth real money. The question is not whether spending is bad. The question is whether the upgrade is worth the option it delays. That is the tradeoff. A $500 monthly upgrade is $6,000 per year. Maybe it is worth it. Maybe it improves your health, safety, work, or relationships enough to justify the cost. But that same $6,000 cannot also build runway, reduce debt pressure, fund investing, or support a future move. This is not guilt. This is clarity. The emotional trap is labeling every upgrade as necessary before testing it. “It is self-care.” “It is an investment.” “I deserve it.” Sometimes yes. Sometimes those words are true. But they can also hide comparison, stress, or avoidance. A strong money life gives the present self real value and the future self real options. That means upgrades need to be chosen on purpose. Ask what improves now, what becomes recurring, what goal slows down, and whether the tradeoff still feels worth it when you can see both sides. Clear spending feels different from automatic spending.',
	flashcards: [
		{
			id: 'big-life-decisions--life-upgrades-need-a-tradeoff--fc-main',
			role: 'main_idea',
			front: 'What is an upgrade tradeoff?',
			back: 'The life value gained compared with the future option delayed.',
		},
		{
			id: 'big-life-decisions--life-upgrades-need-a-tradeoff--fc-watch',
			role: 'watch_out',
			front: 'What hides upgrade costs?',
			back: 'Calling every upgrade self-care, necessary, or an investment without testing it.',
		},
		{
			id: 'big-life-decisions--life-upgrades-need-a-tradeoff--fc-use',
			role: 'use_it',
			front: 'What should you compare?',
			back: 'Compare what improves now against what goal slows down.',
		},
	],
	applyAction: {
		type: 'runFreedomCost',
		label: 'Test a $500 monthly upgrade',
		amount: 500,
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'big-life-decisions',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'big-life-decisions--cq--q1',
			question:
				'What is the main idea of Module 11’s big-decision frame?',
			options: [
				'Only small purchases matter',
				'Big decisions affect timelines, runway, and future options',
				'Emotions should decide every major choice',
				'Monthly payments never matter',
			],
			correctIndex: 1,
			explanation:
				'Big life decisions often change cash flow, runway, flexibility, and future options for months or years.',
		},
		{
			type: 'true_false',
			id: 'big-life-decisions--cq--q2',
			question:
				'A decision can be affordable and still reduce flexibility.',
			correct: true,
			explanation:
				'A cost may fit the monthly budget while still lowering surplus, runway, and future choices.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--cq--q3',
			scenario:
				'A person wants to commit to a $400 monthly payment because it feels like progress.',
			question: 'What should they notice first?',
			options: [
				'Only whether the first payment clears',
				'The yearly cash-flow commitment and what options become harder',
				'Whether friends approve',
				'The brand name',
			],
			correctIndex: 1,
			explanation:
				'A recurring payment affects future cash flow and should be judged by the options it changes.',
		},
		{
			type: 'multiple_choice',
			id: 'big-life-decisions--cq--q4',
			question: 'Why is moving out bigger than a rent decision?',
			options: [
				'Rent is the only cost',
				'Moving out changes bills, runway, privacy, independence, and baseline spending',
				'Moving out is always wrong',
				'Staying home is always better',
			],
			correctIndex: 1,
			explanation:
				'Moving out affects the full monthly life system, not just rent.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--cq--q5',
			scenario:
				'Someone feels embarrassed about still living at home and wants to move mainly to prove adulthood.',
			question: 'What is the emotional trap?',
			options: [
				'Planning from shame instead of clarity',
				'Comparing full costs',
				'Building runway',
				'Testing the decision',
			],
			correctIndex: 0,
			explanation:
				'Shame can push a decision before the person has compared independence, cost, and runway.',
		},
		{
			type: 'true_false',
			id: 'big-life-decisions--cq--q6',
			question:
				'A cheaper or older car is always the better financial decision.',
			correct: false,
			explanation:
				'Cheaper can still be costly if reliability, safety, repairs, or missed work become serious issues.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--cq--q7',
			scenario:
				'A newer car has a $300 higher monthly cost than a reliable cheaper option.',
			question: 'What does Module 11 ask the user to see?',
			options: [
				'Only the monthly payment',
				'The $3,600 yearly gap and what flexibility it affects',
				'The color of the car',
				'Whether the car feels impressive',
			],
			correctIndex: 1,
			explanation:
				'The payment gap is also a yearly flexibility cost that may affect runway and goals.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'big-life-decisions--final--q1',
			question: 'What is a freedom cost?',
			options: [
				'A fee charged by the app',
				'The future option, runway, or flexibility a decision uses up',
				'A guaranteed investment return',
				'A type of credit score',
			],
			correctIndex: 1,
			explanation:
				'Freedom cost means the opportunity or flexibility given up when money is committed to a decision.',
		},
		{
			type: 'true_false',
			id: 'big-life-decisions--final--q2',
			question:
				'The question “Can I afford this?” is useful, but incomplete for big life decisions.',
			correct: true,
			explanation:
				'Big decisions also need questions about runway, flexibility, opportunity cost, stress, and future options.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--final--q3',
			scenario:
				'Alex can move out now, but doing so drops monthly surplus from $700 to $100.',
			question: 'What is the clearest tradeoff?',
			options: [
				'Independence may rise while runway-building slows',
				'Moving out has no financial effect',
				'Staying home is automatically immature',
				'Surplus does not matter',
			],
			correctIndex: 0,
			explanation:
				'The move may buy independence, but it also reduces cash-flow room for runway and goals.',
		},
		{
			type: 'multiple_choice',
			id: 'big-life-decisions--final--q4',
			question: 'What is the strongest way to evaluate moving out?',
			options: [
				'Rent only',
				'Independence gained versus full cost, runway, and flexibility lost',
				'What people online think',
				'Whether the apartment looks cool',
			],
			correctIndex: 1,
			explanation:
				'Moving out is both a life-quality decision and a cash-flow decision.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--final--q5',
			scenario:
				'A car gives someone safer transportation and better work access, but the payment reduces their emergency savings ability.',
			question: 'What is the correct frame?',
			options: [
				'The car is automatically bad',
				'The car is automatically good',
				'The car has a real benefit and a real flexibility cost',
				'Transportation never matters',
			],
			correctIndex: 2,
			explanation:
				'Module 11 frames car choices as tradeoffs between access, safety, cost, and flexibility.',
		},
		{
			type: 'true_false',
			id: 'big-life-decisions--final--q6',
			question:
				'A car payment can raise someone’s monthly baseline even if the car is useful.',
			correct: true,
			explanation:
				'Useful purchases can still create recurring costs that reduce monthly flexibility.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--final--q7',
			scenario:
				'Someone wants to move abroad because rent looks cheaper and life looks exciting online.',
			question: 'What should they compare before trusting the fantasy?',
			options: [
				'Rent only',
				'Income, legal limits, healthcare, support, safety, work options, and lifestyle fit',
				'Photos only',
				'Weather only',
			],
			correctIndex: 1,
			explanation:
				'Location changes the whole life system, not just rent.',
		},
		{
			type: 'multiple_choice',
			id: 'big-life-decisions--final--q8',
			question: 'Why is “cheaper place equals freer place” incomplete?',
			options: [
				'Cheaper places never help',
				'Freedom depends on the full life system, not rent alone',
				'Expensive places are always better',
				'Location does not affect money',
			],
			correctIndex: 1,
			explanation:
				'A cheaper place may reduce costs but can also affect income, support, safety, and opportunity.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--final--q9',
			scenario:
				'A $500 monthly upgrade improves someone’s comfort but delays their move-out fund by a year.',
			question: 'What does Module 11 ask them to decide?',
			options: [
				'Whether the comfort is worth the delayed option',
				'Whether spending is always wrong',
				'Whether the goal should be ignored',
				'Whether all upgrades are self-care',
			],
			correctIndex: 0,
			explanation:
				'Big upgrades need to be compared against the goals they slow down.',
		},
		{
			type: 'true_false',
			id: 'big-life-decisions--final--q10',
			question:
				'Present quality of life matters, but upgrades are stronger when chosen clearly instead of absorbed automatically.',
			correct: true,
			explanation:
				'The module does not reject upgrades. It asks users to see the tradeoff clearly.',
		},
		{
			type: 'multiple_choice',
			id: 'big-life-decisions--final--q11',
			question: 'Which phrase best captures the location lesson?',
			options: [
				'Location is only rent',
				'Location is the cost structure your life runs on',
				'Location is always permanent',
				'Location has no emotional side',
			],
			correctIndex: 1,
			explanation:
				'Location affects expenses, income, support, safety, lifestyle, and opportunity.',
		},
		{
			type: 'scenario',
			id: 'big-life-decisions--final--q12',
			scenario:
				'Sam is choosing between a higher-cost city with stronger career options and a lower-cost city with less career growth.',
			question: 'What is the best Nisk-style interpretation?',
			options: [
				'Pick the cheaper city automatically',
				'Pick the expensive city automatically',
				'Compare what each place makes easier, harder, and more expensive over time',
				'Ignore career and support factors',
			],
			correctIndex: 2,
			explanation:
				'A strong life-design decision compares the full tradeoff instead of assuming one variable decides everything.',
		},
	],
};

export const BIG_LIFE_DECISIONS_MODULE: LearningModule = {
	id: 'big-life-decisions',
	title: 'Big Life Decisions',
	description:
		'Moving out, a car, a relocation, a lifestyle upgrade — big choices are timelines, not just monthly payments. Learn to see the freedom cost of each decision and weigh what it buys against the options it quietly makes harder.',
	emoji: '🧭',
	accentColor: '#c8ff00',
	tier: 'life-design',
	pillar: 'life-decisions',
	order: 11,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
